import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import UserBadge from './UserBadge';
import './style-new.css';

const MakeTeams = () => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const authToken = localStorage.getItem('authToken');

  const [game, setGame] = useState(null);
  const [allPlayers, setAllPlayers] = useState([]);
  const [teams, setTeams] = useState([
    { name: 'Team A', players: [] },
    { name: 'Team B', players: [] }
  ]);
  const [numTeams, setNumTeams] = useState(2);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!authToken) { navigate('/login'); return; }

    const fetchData = async () => {
      try {
        const [gameRes, teamRes, savedTeamsRes] = await Promise.all([
          fetch(`/api/games/${gameId}`, { headers: { Authorization: `Bearer ${authToken}` } }),
          fetch(`/api/games/${gameId}/team`, { headers: { Authorization: `Bearer ${authToken}` } }),
          fetch(`/api/game-teams/${gameId}`, { headers: { Authorization: `Bearer ${authToken}` } })
        ]);

        if (!gameRes.ok || !teamRes.ok || !savedTeamsRes.ok) throw new Error('Failed to fetch data');

        const gameData = await gameRes.json();
        const teamData = await teamRes.json();
        const savedTeams = await savedTeamsRes.json();

        setGame(gameData.game || gameData);

        const mainTeamPlayers = teamData.mainTeam || [];
        setAllPlayers(mainTeamPlayers);

        if (savedTeams.teamsExist && savedTeams.teams) {
          // Load previously saved teams (multi-team format)
          const loadedTeams = savedTeams.teams;
          setNumTeams(loadedTeams.length);
          setTeams(loadedTeams);
        } else if (savedTeams.teamsExist && savedTeams.teamA) {
          // Backwards compat: old 2-team format
          const loadedTeams = [
            { name: savedTeams.teamALabel || 'Team A', players: savedTeams.teamA || [] },
            { name: savedTeams.teamBLabel || 'Team B', players: savedTeams.teamB || [] }
          ];
          setNumTeams(2);
          setTeams(loadedTeams);
        }
      } catch (err) {
        console.error(err);
        alert('Could not load game data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [gameId, authToken]);

  // Adjust number of teams when numTeams changes
const handleNumTeamsChange = (e) => {
  const raw = e.target.value;
  setNumTeams(raw); // allow empty string while typing
  const val = parseInt(raw, 10);
  if (isNaN(val) || val < 1) return;
  setTeams(prev => {
      if (val > prev.length) {
        const extras = Array.from({ length: val - prev.length }, (_, i) => ({
          name: `Team ${String.fromCharCode(65 + prev.length + i)}`,
          players: []
        }));
        return [...prev, ...extras];
      }
      // Shrink — put removed team players back (handled via available pool)
      return prev.slice(0, val);
    });
  };

  // Players already assigned to any team
  const assignedIds = new Set(teams.flatMap(t => t.players.map(p => p.user_id)));

  // Available players for a given team's dropdown (unassigned + already in this team)
  const availableForTeam = (teamIndex) => {
    const thisTeamIds = new Set(teams[teamIndex].players.map(p => p.user_id));
    return allPlayers.filter(p => !assignedIds.has(p.user_id) || thisTeamIds.has(p.user_id));
  };

  const handleAddPlayer = (teamIndex, userId) => {
    if (!userId) return;
    const player = allPlayers.find(p => p.user_id === parseInt(userId, 10));
    if (!player) return;

    setTeams(prev => prev.map((team, i) => {
      if (i === teamIndex) {
        // Add player to this team if not already there
        if (team.players.find(p => p.user_id === player.user_id)) return team;
        return { ...team, players: [...team.players, player] };
      }
      return team;
    }));
  };

  const handleRemovePlayer = (teamIndex, userId) => {
    setTeams(prev => prev.map((team, i) => {
      if (i === teamIndex) {
        return { ...team, players: team.players.filter(p => p.user_id !== userId) };
      }
      return team;
    }));
  };

  const handleTeamNameChange = (teamIndex, value) => {
    setTeams(prev => prev.map((team, i) => i === teamIndex ? { ...team, name: value } : team));
  };

  const handleReset = () => {
    setTeams(prev => prev.map(team => ({ ...team, players: [] })));
  };

  const handleSave = async () => {
    const hasPlayers = teams.some(t => t.players.length > 0);
    if (!hasPlayers) {
      alert('Please assign at least one player to a team before saving.');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`/api/game-teams/${gameId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ teams })
      });
      if (!res.ok) throw new Error('Failed to save teams');
      alert('Teams saved successfully!');
      navigate(`/games/${gameId}`);
    } catch (err) {
      console.error(err);
      alert('Could not save teams. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={styles.loading}>Loading...</div>;
  if (!game) return <div style={styles.loading}>Game not found.</div>;

  const unassignedPlayers = allPlayers.filter(p => !assignedIds.has(p.user_id));

  return (
    <div className="page-container">
      <UserBadge />
      <div className="center-content">
        <h2>Make Teams</h2>
        <p style={{ color: '#555', marginBottom: '4px' }}>{game.description}</p>
        <p style={{ color: '#888', fontSize: '0.9em', marginBottom: '20px' }}>
          Set the number of teams, name them, and assign players using the dropdowns.
        </p>

        {/* Number of teams */}
        <div style={styles.numTeamsRow}>
          <label style={styles.label}>Number of teams</label>
          <input
            type="number"
            min="1"
            max="10"
            value={numTeams}
            onChange={handleNumTeamsChange}
            className="input"
            style={styles.numInput}
          />
        </div>

        {/* Unassigned players count */}
        <p style={{ color: unassignedPlayers.length === 0 ? '#27ae60' : '#e67e22', fontSize: '0.9em', marginBottom: '20px' }}>
          {unassignedPlayers.length === 0
            ? '✅ All players have been assigned!'
            : `⚠️ ${unassignedPlayers.length} player${unassignedPlayers.length > 1 ? 's' : ''} not yet assigned`}
        </p>

        {/* Team sections */}
        <div style={styles.teamsContainer}>
          {teams.slice(0, numTeams).map((team, teamIndex) => {
            const available = availableForTeam(teamIndex);
            const teamColors = ['#5b9bd5', '#e06c6c', '#6cc47e', '#f0a500', '#9b59b6', '#1abc9c'];
            const color = teamColors[teamIndex % teamColors.length];

            return (
              <div key={teamIndex} style={{ ...styles.teamCard, borderTop: `4px solid ${color}` }}>
                {/* Team name input */}
                <input
                  type="text"
                  value={team.name}
                  onChange={e => handleTeamNameChange(teamIndex, e.target.value)}
                  className="input"
                  style={{ ...styles.teamNameInput, borderColor: color }}
                  placeholder={`Team ${teamIndex + 1} name`}
                />

                {/* Add player dropdown */}
                <select
                  className="input"
                  style={styles.dropdown}
                  value=""
                  onChange={e => handleAddPlayer(teamIndex, e.target.value)}
                >
                  <option value="">+ Add player...</option>
                  {available
                    .filter(p => !team.players.find(tp => tp.user_id === p.user_id))
                    .map(p => (
                      <option key={p.user_id} value={p.user_id}>
                        {p.name} {p.surname}
                      </option>
                    ))
                  }
                </select>

                {/* Player list */}
                {team.players.length === 0 ? (
                  <p style={styles.emptyText}>No players added yet</p>
                ) : (
                  <ol style={styles.playerList}>
                    {team.players.map(player => (
                      <li key={player.user_id} style={styles.playerItem}>
                        <span>{player.name} {player.surname}</span>
                        <button
                          onClick={() => handleRemovePlayer(teamIndex, player.user_id)}
                          style={styles.removeBtn}
                        >
                          ✕
                        </button>
                      </li>
                    ))}
                  </ol>
                )}

                <p style={{ fontSize: '0.8em', color: '#aaa', marginTop: '8px' }}>
                  {team.players.length} player{team.players.length !== 1 ? 's' : ''}
                </p>
              </div>
            );
          })}
        </div>

        {/* Buttons */}
        <div className="button-group" style={{ marginTop: '24px' }}>
          <button className="btn btn-grey" onClick={() => navigate(-1)}>⬅ Back</button>
          <button className="btn btn-grey" onClick={handleReset} style={{ marginLeft: '10px' }}>🔄 Reset</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ marginLeft: '10px' }}>
            {saving ? 'Saving...' : '💾 Save Teams'}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  loading: { padding: '20px', textAlign: 'center' },
  numTeamsRow: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' },
  numInput: { width: '70px' },
  label: { fontSize: '0.9em', color: '#555' },
  teamsContainer: { display: 'flex', flexWrap: 'wrap', gap: '16px' },
  teamCard: {
    flex: '1 1 240px',
    backgroundColor: '#f9f9f9',
    border: '1px solid #ddd',
    borderRadius: '8px',
    padding: '16px',
    minWidth: '220px'
  },
  teamNameInput: { width: '100%', marginBottom: '12px', fontWeight: 'bold', fontSize: '1em' },
  dropdown: { width: '100%', marginBottom: '12px' },
  playerList: { paddingLeft: '20px', margin: '0 0 8px 0' },
  playerItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '4px 0',
    fontSize: '0.95em',
    borderBottom: '1px solid #eee'
  },
  removeBtn: {
    background: 'none',
    border: 'none',
    color: '#e74c3c',
    cursor: 'pointer',
    fontSize: '0.9em',
    padding: '2px 6px'
  },
  emptyText: { color: '#bbb', fontSize: '0.85em', fontStyle: 'italic', margin: '8px 0' }
};

export default MakeTeams;
